import React, { useEffect, useState, useRef } from "react";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getGeoRegionalStateEndemicity, getGeoWoredaEndemicity } from "../Services/apiService.js";

export const spinnerOverlayStyle = {
  position: "absolute",
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

export const spinnerStyle = {
  width: "45px",
  height: "45px",
  border: "3px solid #f3f3f3",
  borderTop: "3px solid #024795",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};
export const categoryColors = {
  "<1% prevalence (non-endemic)": "#e6f2ff",
  "Surveillance (prevalence <1%)": "#cce6ff",
  "1 - 9.9% post-IA prevalence": "#99ccff",
  "1 - 9.9% prevalence (low)": "#66b2ff",
  "10 - 49.9% post-IA prevalence": "#3399ff",
  "10 - 49.9% prevalence (moderate)": "#0073e6",
  ">=50% post-IA prevalence": "#0052b3",
  ">=50% prevalence (high)": "#003366",
  "Endemicity unknown": "#99b3cc",
  "No data available": "#cce0ff",
};

const woredaStyle = { color: "#009933", weight: 1, fillOpacity: 0.6 };
const regionStyle = { color: "#003366", weight: 1.5, fillOpacity: 0.1 };
const highlightStyle = { color: "#ff6600", weight: 2, fillOpacity: 0.6 };


export default function EndemicityMap() {
  const [regions, setRegions] = useState(null);
  const [woredas, setWoredas] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const mapRef = useRef();

  useEffect(() => {
    async function fetchData() {
      try {
        const regionalData = await getGeoRegionalStateEndemicity();
        setRegions({
          type: "FeatureCollection",
          features: regionalData.map((r) => ({
            type: "Feature",
            geometry: r.geometry,
            properties: { REGIONNAME: r.regionalState },
          })),
        });
        getGeoWoredaEndemicity(100, (batchData) => {
          const processed = batchData.map((w) => ({
            ...w,
            properties: {
              ...w.properties,
            },
          }));
          setWoredas({ type: "FeatureCollection", features: processed });
        });
      } catch (err) {
        console.error("Failed to fetch geo data:", err);
      }
    }
    fetchData();
  }, []);

  const RegionLayer = ({ data }) => {
    const map = useMap();
    const onEachFeature = (feature, layer) => {
      layer.setStyle(regionStyle);
      layer.on({
        mouseover: () => layer.setStyle({ color: "#ffa500", weight: 2, fillOpacity: 0.3 }),
        mouseout: () => layer.setStyle(regionStyle),
      });
      layer.bindTooltip(feature.properties.REGIONNAME, { permanent: true, direction: "center", className: "region-tooltip" });
      layer.bindPopup(`<b>${feature.properties.REGIONNAME}</b>`);
    };
    return <GeoJSON data={data} onEachFeature={onEachFeature} pane="regionPane" />;
  };
  const WoredaLayer = ({ data, selectedCategory }) => {
  const onEachFeature = (feature, layer) => {
    let endemicity = feature.properties.factors.endemicity;
    let normalizedEndemicity = endemicity ? endemicity.trim() : "No data available";

    const matchesFilter =
      !selectedCategory || normalizedEndemicity === selectedCategory;

    const originalStyle = {
      ...woredaStyle,
      fillColor: matchesFilter
        ? categoryColors[normalizedEndemicity] || "#cccccc"
        : "#dddddd",
      fillOpacity: matchesFilter ? 1 : 0.2,
    };

    layer.setStyle(originalStyle);

    layer.on({
      mouseover: () => {
        if (matchesFilter) layer.setStyle(highlightStyle);
        layer.openPopup();
      },
      mouseout: () => layer.setStyle(originalStyle),
    });

    layer.bindPopup(
      `<b>${feature.properties.WOREDANAME}</b><br/>
       Region: ${feature.properties.REGIONNAME || "Unknown"}<br/>
       Zone: ${feature.properties.ZONENAME || "Unknown"}<br/>
       Endemicity: ${normalizedEndemicity}<br/>`
    );
  };

  return (
    <GeoJSON
      key={selectedCategory}    // <-- forces rerender when category changes
      data={data}
      onEachFeature={onEachFeature}
      pane="woredaPane"
    />
  );
};


  // Legend component to filter endemicity categories
  const Legend = () => {
    const map = useMap();

    useEffect(() => {
      const legend = L.control({ position: "bottomright" });

      legend.onAdd = () => {
        const div = L.DomUtil.create("div", "info legend");
        div.style.background = "#fff";
        div.style.padding = "16px";
        div.style.borderRadius = "5px";
        div.style.fontFamily = "Poppins, sans-serif";
        div.style.fontSize = "14px";
        div.innerHTML = `<p style="font-weight:700; margin-top:0;">Endemicity Categories (2023)</p>`;

        if (selectedCategory) {
          const clearFilter = document.createElement("div");
          clearFilter.style.cursor = "pointer";
          clearFilter.style.marginBottom = "8px";
          clearFilter.style.color = "#024795";
          clearFilter.style.fontWeight = "600";
          clearFilter.innerText = "Clear Filter";
          clearFilter.onclick = () => setSelectedCategory(null);
          div.appendChild(clearFilter);
        }

        // Render category items
        for (const [category, color] of Object.entries(categoryColors)) {
          const item = document.createElement("div");
          item.style.cursor = "pointer";
          item.style.marginBottom = "4px";
          item.style.textDecoration = selectedCategory === category ? "underline" : "none";
          item.innerHTML =
            `<i style="background:${color}; width:18px; height:18px; float:left; margin-right:8px; opacity:0.7; border-radius:3px;"></i>` +
            category;
          item.onclick = () =>
            setSelectedCategory((prev) => (prev === category ? null : category));
          div.appendChild(item);
        }

        return div;
      };

      legend.addTo(map);
      return () => legend.remove();
    }, [map, selectedCategory]);

    return null;
  };

  // Map Layers component
  const MapLayers = () => {
    const map = useMap();

    useEffect(() => {
      // Ensure map panes for regions and woredas exist
      if (!map.getPane("regionPane")) {
        map.createPane("regionPane");
        map.getPane("regionPane").style.zIndex = 400;
      }
      if (!map.getPane("woredaPane")) {
        map.createPane("woredaPane");
        map.getPane("woredaPane").style.zIndex = 450;
      }
    }, [map]);

    return (
      <>
        {regions && <RegionLayer data={regions} />}
        {woredas && <WoredaLayer data={woredas} selectedCategory={selectedCategory} />}
        <Legend/>
      </>
    );
  };

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      {!woredas && (
        <div style={spinnerOverlayStyle}>
          <div style={spinnerStyle}></div>
        </div>
      )}
      <MapContainer
        center={[9.145, 40.4897]}
        zoom={6}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
      >
        <MapLayers />
      </MapContainer>
    </div>
  );
}

